import React, { useState } from 'react';
import { Search, Plus, User, Building2, Mail, Phone, ChevronRight, Folder, Users } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';

const ClientsViewWorldClass = ({ 
  clients = [], 
  searchQuery, 
  setSearchQuery, 
  clientFilter,
  setClientFilter,
  navigate,
  onRefresh
}: { clients?: any[]; searchQuery: any; setSearchQuery: any; clientFilter: any; setClientFilter: any; navigate: any; onRefresh?: any }) => {
  const { t, language } = useTranslation();
  
  const filteredClients = clients.filter(client => {
    const clientName = client.name || client.person_name || client.company_name || '';
    
    const matchesSearch = !searchQuery || 
      clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.phone?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = clientFilter === 'all' || 
      (clientFilter === 'business' && (client.type === 'COMPANY' || client.type === 'BUSINESS')) ||
      (clientFilter === 'private' && (client.type === 'PERSON' || client.type === 'PRIVATE' || client.type === 'INDIVIDUAL'));
    
    return matchesSearch && matchesFilter;
  });

  // Stats
  const totalClients = clients.length;
  const personClients = clients.filter(c => c.type === 'PERSON' || c.type === 'PRIVATE' || c.type === 'INDIVIDUAL').length;
  const companyClients = clients.filter(c => c.type === 'COMPANY' || c.type === 'BUSINESS').length;
  const activeClients = clients.filter(c => c.status === 'ACTIVE').length;

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-8 py-10">
        
        {/* Header */}
        <header className="mb-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
                {language === 'en' ? 'Clients' : 'Klienter'}
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                {filteredClients.length} {filteredClients.length === 1 
                  ? (language === 'en' ? 'client' : 'klient') 
                  : (language === 'en' ? 'clients' : 'klienter')}
              </p>
            </div>
            <button
              onClick={() => navigate('/lexy/clients/new')}
              className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {language === 'en' ? 'New Client' : 'Ny klient'}
            </button>
          </div>
        </header>

        {/* Stats Row */}
        <div className="flex items-center gap-10 mb-10 pb-8 border-b border-gray-100">
          <div>
            <div className="text-3xl font-semibold text-gray-900">{totalClients}</div>
            <div className="text-sm text-gray-500">{language === 'en' ? 'Total' : 'Totalt'}</div>
          </div>
          <div className="w-px h-10 bg-gray-200" />
          <div>
            <div className="text-3xl font-semibold text-gray-900">{personClients}</div>
            <div className="text-sm text-gray-500">{language === 'en' ? 'Individuals' : 'Personer'}</div>
          </div>
          <div className="w-px h-10 bg-gray-200" />
          <div>
            <div className="text-3xl font-semibold text-gray-900">{companyClients}</div>
            <div className="text-sm text-gray-500">{language === 'en' ? 'Companies' : 'Bedrifter'}</div>
          </div>
          <div className="w-px h-10 bg-gray-200" />
          <div>
            <div className="text-3xl font-semibold text-gray-900">{activeClients}</div>
            <div className="text-sm text-gray-500">{language === 'en' ? 'Active' : 'Aktive'}</div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex items-center gap-4 mb-8">
          <div className="flex-1 max-w-md relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={language === 'en' ? 'Search by name or email...' : 'Søk etter navn eller e-post...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
            />
          </div>

          <div className="flex items-center gap-2">
            {['all', 'private', 'business'].map((f) => (
              <button
                key={f}
                onClick={() => setClientFilter(f)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  clientFilter === f
                    ? 'bg-slate-900 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {f === 'all' ? (language === 'en' ? 'All' : 'Alle') :
                 f === 'private' ? (language === 'en' ? 'Individuals' : 'Personer') :
                 (language === 'en' ? 'Companies' : 'Bedrifter')}
              </button>
            ))}
          </div>
        </div>

        {/* Clients List */}
        {filteredClients.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Folder className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-500 text-sm mb-4">
              {searchQuery || clientFilter !== 'all'
                ? (language === 'en' ? 'No clients match your search' : 'Ingen klienter matcher søket')
                : (language === 'en' ? 'No clients yet' : 'Ingen klienter ennå')}
            </p>
            {(!searchQuery && clientFilter === 'all') && (
              <button
                onClick={() => navigate('/lexy/clients/new')}
                className="text-sm text-slate-900 font-medium hover:underline inline-flex items-center gap-1"
              >
                {language === 'en' ? 'Add your first client' : 'Legg til din første klient'}
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-0">
            {filteredClients.map((client) => (
              <div
                key={client.id}
                onClick={() => navigate(`/lexy/clients/${client.id}`)}
                className="group flex items-center gap-4 py-4 cursor-pointer transition-all border-b border-gray-100 last:border-b-0 hover:bg-gray-50 px-3 -mx-3 rounded-lg"
              >
                {/* Icon */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  (client.type === 'PERSON' || client.type === 'PRIVATE' || client.type === 'INDIVIDUAL')
                    ? 'bg-slate-100' 
                    : 'bg-violet-100'
                }`}>
                  {(client.type === 'PERSON' || client.type === 'PRIVATE' || client.type === 'INDIVIDUAL') ? (
                    <User className="w-5 h-5 text-slate-600" />
                  ) : (
                    <Building2 className="w-5 h-5 text-violet-600" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 truncate">
                    {client.name || client.person_name || client.company_name || (language === 'en' ? 'Unnamed Client' : 'Navnløs klient')}
                  </h3>
                  <div className="flex items-center gap-3 mt-0.5">
                    {client.email && (
                      <span className="text-sm text-gray-500 truncate flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {client.email}
                      </span>
                    )}
                    {client.phone && (
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {client.phone}
                      </span>
                    )}
                  </div>
                </div>

                {/* Status & Cases */}
                <div className="flex items-center gap-4">
                  {(client.case_count > 0 || client.active_cases > 0) && (
                    <span className="text-sm text-gray-500">
                      {client.case_count || client.active_cases} {(client.case_count || client.active_cases) === 1 
                        ? (language === 'en' ? 'case' : 'sak') 
                        : (language === 'en' ? 'cases' : 'saker')}
                    </span>
                  )}
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                    client.status === 'ACTIVE'
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {client.status === 'ACTIVE' 
                      ? (language === 'en' ? 'Active' : 'Aktiv') 
                      : (language === 'en' ? 'Inactive' : 'Inaktiv')}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientsViewWorldClass;
